/**
 * SOL.3A-3 — q-aware family cadence calibration authority.
 *
 * ReferenceBasis owns damage/reference normalization; SourceRequirement owns
 * runtime eligibility; the cadence family set owns only action/recast rhythm
 * and mana opportunity-cost calibration. Multi-family cadence uses a
 * permutation-invariant arithmetic middle reference.
 */

import { NonFiniteNumericError } from '../../math.js';
import {
  growthProfileMultiplierAtX,
  midRepeatFullHitMultipleAtX,
  minimumAvailabilityIntervalS,
  resolveLowRepeatAvailability,
  type SkillGrowthProfile,
} from '../../calibration/skill-damage-v13.js';
import { displacedBasicHits, resolveBasicRecoveryS, type DisplacedBasicSource } from '../../calibration/displaced-basic.js';
import { MANA_DAMAGE_V1_PRICE } from '../../calibration/mana-damage-v1.js';
import {
  combatSourceAtProgression,
  completePrimaryStats,
  primaryAllocationForShape,
  type PricingWeaponId,
} from '../../reference/weapon-progression-v0.js';
import { compatibleWeaponFamilies } from '../../sim/source-compatibility.js';
import { resolveSourceProfile, type SourceSnapshot } from '../../sim/source-profile.js';
import { WEAPON_PRIMARY_PROFILE_V0 } from '../../sim/weapon-primary-profile.js';
import {
  WEAPON_FAMILIES,
  type CanonicalAvailabilityPolicy,
  type ReferenceBasis,
  type SourceRequirement,
  type WeaponFamily,
} from '../../types.js';

export const SOL3A3_REFERENCE_LEVEL_MIN = 1;
export const SOL3A3_REFERENCE_LEVEL_MAX = 100;
export const SOL3A3_BRIDGE_MAX_Q_REF = 35;

/**
 * ACTIVE.2 — windup-fraction curve.
 *
 * `windupFraction(q_ref)` is the pre-release share of the `A × T_c` action
 * lock. It is `0` at and below transformed Mid (immediate release, identical
 * to ACTIVE.1) and rises with concentration so a low-frequency high-power
 * activation is visibly "wound up" before it releases. Breakpoints reuse the
 * bridge control points (15 / 35) and the V13 low-repeat anchor coordinates
 * (70 / 200); anchors are provisional and isolated here for re-tuning.
 */
export const ACTIVE2_WINDUP_FRACTION_ANCHORS = Object.freeze({
  q15: 0.20,
  q35: 0.40,
  q70: 0.55,
  q200: 0.70,
});
/**
 * SOL.3A-4 low-repeat q coordinates. `35` is the SOL.3A-3 bridge end and the
 * V13 low-repeat envelope start; `70` is the V13 `very_high` anchor.
 *
 * `200` is `q_max_reference_normal` (V13 §8) — the Normal-profile maximum
 * reference. SOL.3A-5 splits the two distinct jobs this one number used to do:
 *
 * - {@link SOL3A4_Q_SATURATION_ANCHOR} is its **interpolation** role: the top
 *   anchor of the action-cycle and windup curves (the `q200` columns). Those
 *   curves saturate there by construction, so above it both clamp to `q200`
 *   rather than extrapolating.
 * - The **membership** ceiling is now a per-authoring parameter
 *   (`maxSupportedQRef`), defaulting to {@link DEFAULT_MAX_SUPPORTED_Q_REF} for
 *   callers that do not supply one. A skill book's rarity raises it to that
 *   rarity's `activationBurst` cap, so a rarer book can author a longer-cooldown
 *   super-ultimate on the same V13 concentration envelope
 *   (`minimumAvailabilityIntervalS`) rather than a new curve.
 *
 * At and above 200 the V13 contract maps availability to a capacity-1 rolling
 * charge (§7.2, `resolveLowRepeatAvailability`); that region is now authored
 * as `ROLLING_CHARGE_FLOOR` instead of being refused.
 */
const SOL3A4_Q_VERY_HIGH = 70;
const SOL3A4_Q_SATURATION_ANCHOR = 200;
/** Membership ceiling for a caller that supplies no rarity burst cap. */
export const DEFAULT_MAX_SUPPORTED_Q_REF = 200;

/** POWER avoids colliding with WeaponFamily.HEAVY. */
export const SOL3A3_CADENCE_INTENSITY_Q = Object.freeze({ QUICK: 3, CORE: 6.5, POWER: 15, EXTREME: 25 });

/**
 * Broad WEAPON middle used only when a low-level numeric caller has a WEAPON
 * ReferenceBasis but no runtime SourceRequirement yet. Catalyst is excluded:
 * MAGIC has its own ReferenceBasis and cadence fallback. A1 still refuses to
 * persist a runtime skill with no SourceRequirement.
 */
const BROAD_WEAPON_CADENCE_FAMILIES: readonly WeaponFamily[] = Object.freeze(
  WEAPON_FAMILIES.filter((family) => family !== 'CATALYST'),
);
const CATALYST_ONLY: readonly WeaponFamily[] = Object.freeze(['CATALYST']);
const NO_CADENCE_FAMILIES: readonly WeaponFamily[] = Object.freeze([]);

export interface FamilyActionControlSeconds {
  readonly q15: number;
  readonly q25: number;
  readonly q35: number;
  /** SOL.3A-4 low-repeat extension: `70 H_ref` and the `200 H_ref` saturation anchor. */
  readonly q70: number;
  readonly q200: number;
}

/**
 * SOL.3A-4 extends the SOL.3A-3 `15 / 25 / 35 H_ref` action-seconds grid with
 * `70 H_ref` and a `200 H_ref` saturation anchor. The `q70` / `q200` columns
 * are provisional (no direct calibration source — telemetry replaces them);
 * they continue the existing decelerating trend and preserve the ratified
 * family ordering `HEAVY > BALANCED > POLEARM > CATALYST > RANGED > LIGHT`.
 */
export const SOL3A3_FAMILY_ACTION_SECONDS_LV60: Readonly<Record<WeaponFamily, FamilyActionControlSeconds>> = Object.freeze({
  HEAVY: Object.freeze({ q15: 1.60, q25: 2.20, q35: 2.60, q70: 3.40, q200: 3.90 }),
  BALANCED: Object.freeze({ q15: 1.30, q25: 1.90, q35: 2.20, q70: 2.80, q200: 3.20 }),
  POLEARM: Object.freeze({ q15: 1.25, q25: 1.80, q35: 2.10, q70: 2.70, q200: 3.10 }),
  CATALYST: Object.freeze({ q15: 1.15, q25: 1.70, q35: 2.00, q70: 2.60, q200: 3.00 }),
  RANGED: Object.freeze({ q15: 1.05, q25: 1.60, q35: 1.90, q70: 2.50, q200: 2.90 }),
  LIGHT: Object.freeze({ q15: 0.90, q25: 1.35, q35: 1.60, q70: 2.10, q200: 2.40 }),
});

export interface CadenceCalibration {
  readonly level: number;
  readonly families: readonly WeaponFamily[];
  readonly hitDamage: number;
  readonly attackRatePerS: number;
  readonly actionCycleS: number;
  readonly recastCycleS: number;
  /** The basic attack the action lock displaces (`resolveDisplacedBasicSource`); its hit may be another family's than `hitDamage`. */
  readonly displacedBasic: DisplacedBasicSource;
  readonly controlCycles: { readonly q15: number; readonly q25: number; readonly q35: number; readonly q70: number; readonly q200: number };
}

/**
 * SOL.3A-5 — why an activation is not cadence-supported. The two cases carry
 * different meaning for the player and must not be collapsed:
 *
 * - `ABOVE_BOOK_CEILING`: the concentration envelope prices this q perfectly
 *   well; the authoring **skill book** just does not allow that much in one
 *   activation. Nothing is missing from the system, so this is a user-policy
 *   refusal ("not possible within this book's limits — lower the spec"), not
 *   a deferral.
 * - `REGION_UNDEFINED`: no cadence curve is defined for this shape at all
 *   (e.g. a transformed Mid at or above the POWER intensity coordinate).
 *   That is a real system limitation and stays a deferral.
 */
export type CadenceUnsupportedReason = 'ABOVE_BOOK_CEILING' | 'REGION_UNDEFINED';

export interface CadenceTimingResolution {
  readonly status: 'SUPPORTED' | 'UNSUPPORTED';
  /** Present only when `status` is `UNSUPPORTED`. */
  readonly unsupportedReason?: CadenceUnsupportedReason;
  /** Present only for `ABOVE_BOOK_CEILING` — the ceiling that refused it. */
  readonly maxSupportedQRef?: number;
  readonly qRef: number;
  readonly qCadence: number;
  readonly qMidRef: number;
  readonly actionCycles?: number;
  readonly actionTimeS?: number;
  /** ACTIVE.2 pre-release share of the action lock, in [0, 1]. */
  readonly windupFraction?: number;
  readonly styleDefaultRecastS?: number;
  readonly sustainedFloorS?: number;
  readonly concentrationFloorS?: number;
  readonly minimumRecastS?: number;
  /** Persisted runtime legality fact, resolved from the same owned q boundary. */
  readonly availability?: CanonicalAvailabilityPolicy;
}

function requireFinitePositive(value: number, label: string): void {
  if (!Number.isFinite(value)) throw new NonFiniteNumericError(`${label} must be finite > 0 (got ${value})`);
  if (value <= 0) throw new RangeError(`${label} must be finite > 0 (got ${value})`);
}
function requireReferenceLevel(level: number): void {
  if (!Number.isFinite(level) || level < SOL3A3_REFERENCE_LEVEL_MIN || level > SOL3A3_REFERENCE_LEVEL_MAX) {
    throw new RangeError(`reference level must be finite in [${SOL3A3_REFERENCE_LEVEL_MIN}, ${SOL3A3_REFERENCE_LEVEL_MAX}] (got ${level})`);
  }
}
function mean(values: readonly number[], label: string): number {
  if (values.length === 0) throw new Error(`${label} requires at least one value`);
  const value = values.reduce((sum, item) => sum + item, 0) / values.length;
  requireFinitePositive(value, label);
  return value;
}

export function referenceWeaponPowerMultiplier(level: number): number {
  requireReferenceLevel(level);
  return level <= 20 ? 1 : Math.pow(2, (level - 20) / 40);
}

export function resolveFamilyReferenceSnapshot(family: WeaponFamily, level: number): SourceSnapshot {
  requireReferenceLevel(level);
  const raw = WEAPON_PRIMARY_PROFILE_V0.referenceSources[family];
  return resolveSourceProfile(
    {
      id: `sol3a3.reference.${family.toLowerCase()}.L${level}`,
      family,
      baseDamage: raw.baseDamage * referenceWeaponPowerMultiplier(level),
      baseAttackRate: raw.baseAttackRate,
    },
    completePrimaryStats(primaryAllocationForShape(family, [3 * level, level, level])),
    WEAPON_PRIMARY_PROFILE_V0,
  );
}

/** A concrete weapon (subtype raw values included) at the same reference level and stat shape as its family reference. */
export function resolveWeaponReferenceSnapshot(weaponId: PricingWeaponId, level: number): SourceSnapshot {
  requireReferenceLevel(level);
  const source = combatSourceAtProgression(weaponId, level, referenceWeaponPowerMultiplier(level));
  return resolveSourceProfile(
    source,
    completePrimaryStats(primaryAllocationForShape(source.family, [3 * level, level, level])),
    WEAPON_PRIMARY_PROFILE_V0,
  );
}

export function resolveHRef(level: number): number {
  return resolveFamilyReferenceSnapshot('BALANCED', level).hitDamage;
}

/**
 * Cadence family membership is separate from damage ReferenceBasis authority.
 * An existing SourceRequirement selects its effective runtime-compatible
 * family set. When the requirement is not resolved yet, a low-level numeric
 * preview may use the broad middle implied by the already-authoritative
 * ReferenceBasis; this fallback never authors runtime eligibility.
 */
export function resolveCadenceFamilySet(
  requirement: SourceRequirement | undefined,
  fallbackReferenceBasis?: ReferenceBasis,
): readonly WeaponFamily[] {
  if (requirement === undefined) {
    if (fallbackReferenceBasis === 'MAGIC') return CATALYST_ONLY;
    if (fallbackReferenceBasis === 'WEAPON') return BROAD_WEAPON_CADENCE_FAMILIES;
    return NO_CADENCE_FAMILIES;
  }
  if (requirement.sourceClass === 'BODY') return NO_CADENCE_FAMILIES;
  if (requirement.sourceClass === 'MAGIC') return CATALYST_ONLY;
  return Object.freeze([...compatibleWeaponFamilies(requirement)]);
}

function resolveCompositeCycleS(families: readonly WeaponFamily[], level: number): number {
  return mean(families.map((family) => resolveFamilyReferenceSnapshot(family, level).actionCycleS), 'composite cadence cycle');
}
function resolveCompositeControlCycles(families: readonly WeaponFamily[]): CadenceCalibration['controlCycles'] {
  const referenceCycle60 = resolveCompositeCycleS(families, 60);
  const q15S = mean(families.map((family) => SOL3A3_FAMILY_ACTION_SECONDS_LV60[family].q15), 'q15 mean action seconds');
  const q25S = mean(families.map((family) => SOL3A3_FAMILY_ACTION_SECONDS_LV60[family].q25), 'q25 mean action seconds');
  const q35S = mean(families.map((family) => SOL3A3_FAMILY_ACTION_SECONDS_LV60[family].q35), 'q35 mean action seconds');
  const q70S = mean(families.map((family) => SOL3A3_FAMILY_ACTION_SECONDS_LV60[family].q70), 'q70 mean action seconds');
  const q200S = mean(families.map((family) => SOL3A3_FAMILY_ACTION_SECONDS_LV60[family].q200), 'q200 mean action seconds');
  return Object.freeze({
    q15: q15S / referenceCycle60,
    q25: q25S / referenceCycle60,
    q35: q35S / referenceCycle60,
    q70: q70S / referenceCycle60,
    q200: q200S / referenceCycle60,
  });
}

/**
 * The basic attack a skill's action lock displaces. With `pricingWeaponId`, that weapon at `level` (the canonical anchor);
 * otherwise a MAGIC cast displaces `CATALYST_REFERENCE`'s basic and everything else the BALANCED family reference's (the
 * SWORD row), at the same `level` — never nothing.
 */
export function resolveDisplacedBasicSource(magic: boolean, level: number, pricingWeaponId?: PricingWeaponId): DisplacedBasicSource {
  const weaponId: PricingWeaponId | undefined = pricingWeaponId ?? (magic ? 'CATALYST_REFERENCE' : undefined);
  const snapshot = weaponId === undefined ? resolveFamilyReferenceSnapshot('BALANCED', level) : resolveWeaponReferenceSnapshot(weaponId, level);
  return Object.freeze({
    hitDamage: snapshot.hitDamage,
    cycleS: snapshot.actionCycleS,
    recoveryS: resolveBasicRecoveryS(weaponId ?? 'SWORD', snapshot.actionCycleS),
  });
}

/**
 * The basic hits an action lock displaces (`displacedBasicHits` of the calibration's displaced basic), in the
 * calibration's own hit equivalents: the displaced basic may be another family's than the calibration. The cadence
 * floor and the mana increment both subtract this count.
 */
export function displacedCalibrationHitEquivalents(actionTimeS: number, calibration: CadenceCalibration): number {
  return displacedBasicHits(actionTimeS, calibration.displacedBasic) * calibration.displacedBasic.hitDamage / calibration.hitDamage;
}

export function resolveCadenceCalibration(
  requirement: SourceRequirement | undefined,
  level: number,
  fallbackReferenceBasis?: ReferenceBasis,
  /** Opt-in: price hit and cycle on this weapon instead of the family mean. */
  pricingWeaponId?: PricingWeaponId,
): CadenceCalibration | undefined {
  requireReferenceLevel(level);
  const families = resolveCadenceFamilySet(requirement, fallbackReferenceBasis);
  if (families.length === 0) return undefined;
  const snapshots = pricingWeaponId !== undefined
    ? [resolveWeaponReferenceSnapshot(pricingWeaponId, level)]
    : families.map((family) => resolveFamilyReferenceSnapshot(family, level));
  const cycleS = mean(snapshots.map((snapshot) => snapshot.actionCycleS), 'cadence action cycle');
  const hitDamage = mean(snapshots.map((snapshot) => snapshot.hitDamage), 'cadence hit damage');
  return Object.freeze({
    level,
    families,
    hitDamage,
    attackRatePerS: 1 / cycleS,
    actionCycleS: cycleS,
    recastCycleS: cycleS,
    displacedBasic: resolveDisplacedBasicSource(
      requirement?.sourceClass === 'MAGIC' || (requirement === undefined && fallbackReferenceBasis === 'MAGIC'), level, pricingWeaponId,
    ),
    controlCycles: resolveCompositeControlCycles(families),
  });
}

export function normalizedActivationQRef(damageM10AtGrowth: number, profile: SkillGrowthProfile, growthX: number, level: number): number {
  requireFinitePositive(damageM10AtGrowth, 'damageM10AtGrowth');
  const S = growthProfileMultiplierAtX(profile, growthX);
  requireFinitePositive(S, 'growth profile multiplier');
  return damageM10AtGrowth / (S * resolveHRef(level));
}
export function cadenceHitMultiple(damageM10AtGrowth: number, calibration: CadenceCalibration): number {
  requireFinitePositive(damageM10AtGrowth, 'damageM10AtGrowth');
  return damageM10AtGrowth / calibration.hitDamage;
}

/**
 * SOL.3A-5 — the concentration coordinate the V13 low-repeat envelope is
 * driven by: **how many of the wielder's own basic attacks one activation is
 * worth**.
 *
 * This replaces {@link normalizedActivationQRef}'s fixed `H_ref` denominator
 * for cadence purposes. `damageM10AtGrowth` is realized on a specific anchor
 * snapshot, so dividing it by a fixed constant compared a quantity produced by
 * one wielder against another wielder's basic attack. Dividing by that same
 * anchor's own hit is self-consistent by construction, and it is the only
 * denominator under which the two things a skill's cooldown must respect both
 * hold:
 *
 *   - within one wielder, more absolute damage means a larger multiple and so
 *     a longer interval;
 *   - across wielders, the same absolute damage is a *different* skill — 20,000
 *     is 235 basic attacks to a novice and 99 to a veteran, an ultimate to one
 *     and a repeat skill to the other, and their intervals must differ
 *     accordingly.
 *
 * It is also the only denominator under which two identical artifacts price
 * identically: `F`/`K` are per-wielder-hit multipliers, so an authored
 * `F = K = N` skill is "N times your own hit" for *every* holder. `N` is
 * therefore wielder-invariant, and baking a single interval into the skill at
 * authoring time stays coherent for everyone who later equips it.
 *
 * Burst deliberately keeps the fixed `H_ref` denominator
 * (`burst/types.ts` — caps are Reference-Basic-Attack-equivalent, never raw
 * damage): "how much absolute punch lands in one blow" must be the same ruler
 * for everybody. The two axes measure different questions and now say so.
 */
export function ownHitConcentration(
  damageM10AtGrowth: number,
  profile: SkillGrowthProfile,
  growthX: number,
  anchorHitDamage: number,
): number {
  requireFinitePositive(damageM10AtGrowth, 'damageM10AtGrowth');
  requireFinitePositive(anchorHitDamage, 'anchorHitDamage');
  const S = growthProfileMultiplierAtX(profile, growthX);
  requireFinitePositive(S, 'growth profile multiplier');
  return damageM10AtGrowth / (S * anchorHitDamage);
}

/**
 * The Mid boundary on {@link ownHitConcentration}'s axis. On the own-hit axis
 * the anchor's `hitDamage` cancels out of {@link transformedMidQRef} entirely,
 * so transformed Mid is simply the V13 Mid full-hit multiple — "Mid repeat is
 * 6.5x your own basic attack", true for every wielder.
 */
export function transformedMidOwnHitMultiple(profile: SkillGrowthProfile, growthX: number): number {
  return midRepeatFullHitMultipleAtX(profile, growthX);
}

/**
 * Exact Mid lower boundary on the SOURCE.1 damage/reference axis. Cadence
 * family calibration must not redefine this q coordinate.
 */
export function transformedMidQRef(
  profile: SkillGrowthProfile,
  growthX: number,
  sourceReferenceHitDamage: number,
  level: number,
): number {
  requireFinitePositive(sourceReferenceHitDamage, 'sourceReferenceHitDamage');
  const S = growthProfileMultiplierAtX(profile, growthX);
  requireFinitePositive(S, 'growth profile multiplier');
  return midRepeatFullHitMultipleAtX(profile, growthX) * sourceReferenceHitDamage / (S * resolveHRef(level));
}

function lerp(x: number, x0: number, y0: number, x1: number, y1: number): number {
  if (!(x1 > x0)) throw new Error(`cadence interpolation requires x1>x0 (got ${x0}, ${x1})`);
  return y0 + ((x - x0) / (x1 - x0)) * (y1 - y0);
}

/**
 * Normalize only machine-precision drift around a ratified hard membership
 * boundary. This is not a potency clamp: materially out-of-domain q remains
 * unsupported and the original qRef is retained in diagnostic evidence.
 */
function snapNearOwnedBoundary(value: number, boundary: number): number {
  const tolerance = Number.EPSILON * 16 * Math.max(1, Math.abs(value), Math.abs(boundary));
  return Math.abs(value - boundary) <= tolerance ? boundary : value;
}

/**
 * ACTIVE.2 — pre-release fraction of the `A × T_c` action lock for a given
 * concentration. Monotonic, piecewise-linear, `0` at and below transformed
 * Mid, and clamped to its top anchor at and above the maximum reference q.
 * Defined across the whole low-repeat range now; `resolveCadenceTiming` only
 * consumes it inside the currently supported membership region.
 */
export function resolveWindupFraction(qRef: number, qMidRef: number): number {
  requireFinitePositive(qRef, 'qRef');
  requireFinitePositive(qMidRef, 'qMidRef');
  const { q15, q35, q70, q200 } = ACTIVE2_WINDUP_FRACTION_ANCHORS;
  const power = SOL3A3_CADENCE_INTENSITY_Q.POWER;
  if (qRef <= qMidRef || !(qMidRef < power)) return 0;
  if (qRef <= power) return lerp(qRef, qMidRef, 0, power, q15);
  if (qRef <= SOL3A3_BRIDGE_MAX_Q_REF) return lerp(qRef, power, q15, SOL3A3_BRIDGE_MAX_Q_REF, q35);
  if (qRef <= SOL3A4_Q_VERY_HIGH) return lerp(qRef, SOL3A3_BRIDGE_MAX_Q_REF, q35, SOL3A4_Q_VERY_HIGH, q70);
  if (qRef < SOL3A4_Q_SATURATION_ANCHOR) return lerp(qRef, SOL3A4_Q_VERY_HIGH, q70, SOL3A4_Q_SATURATION_ANCHOR, q200);
  return q200;
}

/**
 * Piecewise-linear action-cycle curve. SOL.3A-3 covers `qMid_ref .. 35`;
 * SOL.3A-4 extends it through `35 .. 70` and `70 .. <200` using the new
 * family action-seconds anchors.
 *
 * SOL.3A-5: at and above {@link SOL3A4_Q_SATURATION_ANCHOR} the curve clamps to
 * its `q200` column instead of returning undefined. That column is the
 * *saturation* anchor of the family action-seconds table
 * (`phase-sol3a4-low-repeat-cadence-region.md §3.1` labels it
 * "200 H (saturation)"), so holding it above 200 is what the anchor already
 * means — extrapolating the 70→200 slope past its top anchor is not.
 * A rarity ceiling above 200 therefore buys a longer minimum interval
 * (`minimumAvailabilityIntervalS` keeps rising) but no further action lock.
 */
function bridgeActionCycles(qRef: number, qMidRef: number, calibration: CadenceCalibration): number | undefined {
  if (!(qMidRef < SOL3A3_CADENCE_INTENSITY_Q.POWER)) return undefined;
  const { q15, q25, q35, q70, q200 } = calibration.controlCycles;
  if (qRef <= SOL3A3_CADENCE_INTENSITY_Q.POWER) return lerp(qRef, qMidRef, 1, SOL3A3_CADENCE_INTENSITY_Q.POWER, q15);
  if (qRef <= SOL3A3_CADENCE_INTENSITY_Q.EXTREME) return lerp(qRef, SOL3A3_CADENCE_INTENSITY_Q.POWER, q15, SOL3A3_CADENCE_INTENSITY_Q.EXTREME, q25);
  if (qRef <= SOL3A3_BRIDGE_MAX_Q_REF) return lerp(qRef, SOL3A3_CADENCE_INTENSITY_Q.EXTREME, q25, SOL3A3_BRIDGE_MAX_Q_REF, q35);
  if (qRef <= SOL3A4_Q_VERY_HIGH) return lerp(qRef, SOL3A3_BRIDGE_MAX_Q_REF, q35, SOL3A4_Q_VERY_HIGH, q70);
  if (qRef < SOL3A4_Q_SATURATION_ANCHOR) return lerp(qRef, SOL3A4_Q_VERY_HIGH, q70, SOL3A4_Q_SATURATION_ANCHOR, q200);
  return q200;
}

export function resolveCadenceTiming(
  qRef: number,
  qCadence: number,
  qMidRef: number,
  calibration: CadenceCalibration,
  seedActionCycles: number,
  seedRecastCycles: number,
  maxSupportedQRef: number = DEFAULT_MAX_SUPPORTED_Q_REF,
): CadenceTimingResolution {
  requireFinitePositive(qRef, 'qRef');
  requireFinitePositive(qCadence, 'qCadence');
  requireFinitePositive(qMidRef, 'qMidRef');
  requireFinitePositive(seedActionCycles, 'seedActionCycles');
  requireFinitePositive(seedRecastCycles, 'seedRecastCycles');
  requireFinitePositive(maxSupportedQRef, 'maxSupportedQRef');
  if (maxSupportedQRef < DEFAULT_MAX_SUPPORTED_Q_REF) {
    // A rarity ceiling may only widen the membership region, never narrow it
    // below the Normal-profile maximum reference every book already reaches.
    throw new Error(
      `resolveCadenceTiming: maxSupportedQRef ${maxSupportedQRef} is below the ${DEFAULT_MAX_SUPPORTED_Q_REF} H_ref floor`,
    );
  }

  // Mid and 35H_ref are ratified membership boundaries. Snap only
  // machine-precision drift around those owned coordinates; materially
  // different q remains untouched and therefore cannot gain potency/cadence
  // authority by tolerance.
  //
  // SOL.3A-5 adds the ceiling to that chain. Under SOL.3A-4 the 200H edge was
  // deliberately left unsnapped because it was a pure deferral edge that no
  // authored request was ever meant to land on. It is now the rarity's own
  // ratified `activationBurst` cap, and a request for exactly that cap is the
  // normal top-of-book case — `q_ref` for an EXACT 250H request reaches
  // 250.00000000000003 through the growth/H_ref normalization, which would
  // otherwise refuse the very skill the rarity exists to allow.
  const effectiveQRef = snapNearOwnedBoundary(
    snapNearOwnedBoundary(snapNearOwnedBoundary(qRef, qMidRef), SOL3A3_BRIDGE_MAX_Q_REF),
    maxSupportedQRef,
  );
  // SOL.3A-5: the membership ceiling is the authoring caller's rarity burst
  // cap (`maxSupportedQRef`), not a fixed 200. Note this bound is inclusive —
  // 200 H_ref itself is the exact point where `minimumAvailabilityIntervalS`
  // reaches 86,400s, so excluding it would leave the concentration envelope's
  // own endpoint unauthorable.
  if (effectiveQRef > maxSupportedQRef) {
    return { status: 'UNSUPPORTED', unsupportedReason: 'ABOVE_BOOK_CEILING', maxSupportedQRef, qRef, qCadence, qMidRef };
  }

  // Exact transformed Mid is owned by the ratified V13 Mid cadence anchor
  // (1.0T), even when a qualitative frequency objective selected the High
  // seed for values strictly below Mid. This keeps the bridge continuous at
  // its lower membership boundary without changing sub-Mid High behavior.
  const actionCycles = effectiveQRef < qMidRef
    ? seedActionCycles
    : bridgeActionCycles(effectiveQRef, qMidRef, calibration);
  if (actionCycles === undefined) {
    return { status: 'UNSUPPORTED', unsupportedReason: 'REGION_UNDEFINED', qRef, qCadence, qMidRef };
  }

  const actionTimeS = actionCycles * calibration.actionCycleS;
  const opportunity = displacedCalibrationHitEquivalents(actionTimeS, calibration);
  const sustainedFloorS = Math.max(
    0,
    (qCadence - opportunity) /
      (MANA_DAMAGE_V1_PRICE.sustainedIncrementalBasicDpsCeiling * calibration.attackRatePerS),
  );
  const styleDefaultRecastS = seedRecastCycles * calibration.recastCycleS;
  let concentrationFloorS = 0;
  if (effectiveQRef >= qMidRef && effectiveQRef <= SOL3A3_BRIDGE_MAX_Q_REF) {
    // SOL.3A-3 bridge concentration envelope (qMid_ref .. 35 -> 6.25T_c .. 60s).
    const tMid = 6.25 * calibration.recastCycleS;
    const u = (effectiveQRef - qMidRef) / (SOL3A3_BRIDGE_MAX_Q_REF - qMidRef);
    concentrationFloorS = tMid * Math.exp(Math.log(MANA_DAMAGE_V1_PRICE.bridgeEndMinimumRecastS / tMid) * u);
  } else if (effectiveQRef > SOL3A3_BRIDGE_MAX_Q_REF) {
    // SOL.3A-4: above the bridge end the V13 low-repeat concentration envelope
    // owns the minimum interval. Continuous at q=35 (both resolve to 60s).
    concentrationFloorS = minimumAvailabilityIntervalS(effectiveQRef);
  }
  const minimumRecastS = Math.max(sustainedFloorS, concentrationFloorS);
  const windupFraction = resolveWindupFraction(effectiveQRef, qMidRef);
  let availability: CanonicalAvailabilityPolicy;
  if (effectiveQRef < SOL3A3_BRIDGE_MAX_Q_REF) {
    availability = Object.freeze({ kind: 'SOURCE_RELATIVE_ONLY' });
  } else if (effectiveQRef === SOL3A3_BRIDGE_MAX_Q_REF) {
    availability = Object.freeze({ kind: 'ORDINARY_COOLDOWN_FLOOR', minimumIntervalS: MANA_DAMAGE_V1_PRICE.bridgeEndMinimumRecastS });
  } else {
    // SOL.3A-4/5: above the bridge end, persist the V13 absolute low-repeat
    // floor (authored data, not a quantity runtime reconstructs from live
    // source). V13 §7.2 splits the region at 200 H_ref: below it an ordinary
    // cooldown, at and above it a capacity-1 rolling charge, so that a
    // multi-day interval cannot be reset by a calendar boundary.
    const lowRepeat = resolveLowRepeatAvailability(effectiveQRef);
    availability = lowRepeat.kind === 'ordinary_cooldown'
      ? Object.freeze({ kind: 'ORDINARY_COOLDOWN_FLOOR', minimumIntervalS: lowRepeat.minimumAvailabilityIntervalS })
      : Object.freeze({
          kind: 'ROLLING_CHARGE_FLOOR',
          minimumIntervalS: lowRepeat.minimumAvailabilityIntervalS,
          capacity: 1,
          rollingRechargeS: lowRepeat.rollingRechargeS ?? lowRepeat.minimumAvailabilityIntervalS,
        });
  }

  return {
    status: 'SUPPORTED', qRef, qCadence, qMidRef, actionCycles, actionTimeS, windupFraction,
    styleDefaultRecastS, sustainedFloorS, concentrationFloorS, minimumRecastS,
    availability,
  };
}
